import type { AttestDecision } from '@/components/session/ConsentAttestControl.types';
import { useToast } from '@/hooks';
import { useState } from 'react';

/** Confirmation copy for a consent attestation, shared by every consent control. */
export const ATTEST_COPY: Record<AttestDecision, { title: string; description: string; action: string }> = {
  permit: {
    title: 'Staðfesta skriflegt samþykki',
    description:
      'Með því að halda áfram staðfestir þú að sjúklingurinn hafi undirritað skriflegt samþykki fyrir notkun talgreiningar. Aðgerðin er skráð í sjúkraskrá á þína kennitölu.',
    action: 'Staðfesta',
  },
  deny: {
    title: 'Skrá synjun á samþykki',
    description:
      'Með því að halda áfram staðfestir þú að sjúklingurinn hafi hafnað skriflegu samþykki fyrir notkun talgreiningar. Aðgerðin er skráð í sjúkraskrá á þína kennitölu.',
    action: 'Skrá synjun',
  },
};

interface UseConsentAttestArgs {
  onAttest: (decision: AttestDecision) => Promise<unknown>;
  isAttesting: boolean;
  /** Runs after a successful attestation, once the decision is cleared. */
  onDone?: () => void;
}

/**
 * The pending decision and its confirm step: records the attestation through
 * `onAttest` and toasts the outcome. The controls only differ in how they ask.
 */
export const useConsentAttest = ({ onAttest, isAttesting, onDone }: UseConsentAttestArgs) => {
  const { toast } = useToast();
  const [decision, setDecision] = useState<AttestDecision | null>(null);

  const confirm = () => {
    if (!decision || isAttesting) return;
    onAttest(decision).then(
      () => {
        toast({
          title: decision === 'permit' ? 'Samþykki staðfest' : 'Synjun skráð',
          description:
            decision === 'permit' ? 'Skriflegt samþykki hefur verið staðfest.' : 'Synjun á samþykki hefur verið skráð.',
        });
        setDecision(null);
        onDone?.();
      },
      () => {
        toast({ title: 'Villa', description: 'Ekki tókst að uppfæra samþykki. Reyndu aftur.', variant: 'destructive' });
      }
    );
  };

  return { decision, setDecision, confirm };
};
